import { ref } from 'vue'

export const COMPOSER_MODES = Object.freeze(['compose', 'practice'])
export const COMPOSER_MODE_SHORTCUTS = Object.freeze({ F1: 'compose', F2: 'practice' })
export const COMPOSER_KEYBOARD_BASE_MIDI = 48

// Chromatic tracker layout: lower row starts at the base note, upper row one octave higher.
const LOWER_ROW = ['z', 's', 'x', 'd', 'c', 'v', 'g', 'b', 'h', 'n', 'j', 'm']
const UPPER_ROW = ['q', '2', 'w', '3', 'e', 'r', '5', 't', '6', 'y', '7', 'u']

export const COMPOSER_KEYBOARD_SEMITONES = Object.freeze(Object.fromEntries([
  ...LOWER_ROW.map((key, index) => [key, index]),
  ...UPPER_ROW.map((key, index) => [key, index + 12]),
]))

export const COMPOSER_KEYBOARD_SPAN = LOWER_ROW.length + UPPER_ROW.length

// Alt+digit keeps the plain digits available as piano keys. Values are the canonical
// grid divisions the Composer duration selector already uses.
export const INSERT_DURATION_SHORTCUTS = Object.freeze({
  Digit1: '1/1',
  Digit2: '1/2',
  Digit3: '1/4',
  Digit4: '1/8',
  Digit5: '1/16',
  Digit6: '1/32',
})

export function composerModeForShortcut(key) {
  return COMPOSER_MODE_SHORTCUTS[String(key)] ?? null
}

export function insertDurationForShortcut(code) {
  return INSERT_DURATION_SHORTCUTS[String(code)] ?? null
}

export function keyboardSemitone(key) {
  const semitone = COMPOSER_KEYBOARD_SEMITONES[String(key).toLowerCase()]
  return Number.isInteger(semitone) ? semitone : null
}

export function keyboardMidi(key, baseMidi = COMPOSER_KEYBOARD_BASE_MIDI) {
  const semitone = keyboardSemitone(key)
  if (semitone == null) return null
  const midi = Math.trunc(Number(baseMidi) || 0) + semitone
  return midi >= 0 && midi <= 127 ? midi : null
}

export function clampKeyboardBaseMidi(baseMidi, minMidi = 0, maxMidi = 127) {
  const lowest = Math.max(0, Math.trunc(Number(minMidi) || 0))
  const highest = Math.max(lowest, Math.min(127, Math.trunc(Number(maxMidi) || 127)) - (COMPOSER_KEYBOARD_SPAN - 1))
  return Math.min(highest, Math.max(lowest, Math.trunc(Number(baseMidi) || 0)))
}

export function snapTickToGrid(tick, gridTicks) {
  const grid = Math.max(1, Math.trunc(Number(gridTicks) || 0))
  return Math.max(0, Math.round(Math.max(0, Number(tick) || 0) / grid) * grid)
}

// Note navigation works on unique note start positions, so a chord counts as one stop.
// Returns null when there is no note in that direction: navigation never wraps.
export function findAdjacentNoteStart(sequence, tick, direction) {
  const cursor = Math.max(0, Number(tick) || 0)
  const forward = Math.trunc(Number(direction) || 0) >= 0
  let best = null
  for (const event of sequence ?? []) {
    if (event?.note == null) continue
    const startTick = Math.max(0, Number(event.startTick) || 0)
    if (forward ? startTick <= cursor : startTick >= cursor) continue
    if (best == null || (forward ? startTick < best : startTick > best)) best = startTick
  }
  return best
}

export function findNoteEventsAtStart(sequence, tick) {
  const startTick = Math.max(0, Number(tick) || 0)
  return (sequence ?? []).filter(event => event?.note != null && Math.max(0, Number(event.startTick) || 0) === startTick)
}

// Step-entry delete target: the latest note event that still reaches the insert cursor.
// A gap before the cursor yields -1 so Backspace never removes an unrelated event further
// back in the song; ties on startTick pick the most recently inserted voice of a chord.
export function findPreviousInsertEventIndex(sequence, insertTick) {
  const cursor = Math.max(0, Number(insertTick) || 0)
  let best = -1
  let bestStart = -1
  for (let index = 0; index < (sequence?.length ?? 0); index++) {
    const event = sequence[index]
    if (event?.note == null) continue
    const startTick = Math.max(0, Number(event.startTick) || 0)
    const endTick = startTick + Math.max(1, Number(event.durationTicks) || 0)
    if (startTick >= cursor || endTick < cursor) continue
    if (startTick >= bestStart) {
      best = index
      bestStart = startTick
    }
  }
  return best
}

export function isTextInputTarget(target) {
  if (!target || typeof target !== 'object') return false
  if (target.isContentEditable) return true
  const tag = String(target.tagName ?? '').toLowerCase()
  return tag === 'input' || tag === 'textarea' || tag === 'select'
}

export function createComposerModeState(initialMode = 'compose') {
  const composerMode = ref(COMPOSER_MODES.includes(initialMode) ? initialMode : 'compose')

  function setComposerMode(mode) {
    if (!COMPOSER_MODES.includes(mode)) return false
    composerMode.value = mode
    return true
  }

  return { composerMode, setComposerMode }
}

export function createComposerInsertCursor(initialTick = 0) {
  const insertTick = ref(Math.max(0, Math.trunc(Number(initialTick) || 0)))

  function setInsertTick(tick, gridTicks) {
    const next = gridTicks == null ? Math.max(0, Math.round(Number(tick) || 0)) : snapTickToGrid(tick, gridTicks)
    if (!Number.isFinite(next)) return false
    insertTick.value = next
    return true
  }

  function advanceInsertTick(durationTicks) {
    const step = Math.max(1, Math.trunc(Number(durationTicks) || 0))
    insertTick.value = Math.max(0, insertTick.value + step)
    return insertTick.value
  }

  function resetInsertTick() {
    insertTick.value = 0
  }

  return { insertTick, setInsertTick, advanceInsertTick, resetInsertTick }
}
